import React, { useState, useEffect } from 'react';
import { useSocket } from '../../context/SocketContext';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, IndianRupee, Hash, Trash2, ReceiptText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { format } from 'date-fns';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const socket = useSocket();
  const navigate = useNavigate();

  useEffect(() => { fetchOrders(); }, []);

  useEffect(() => {
    if (socket) {
      socket.on('new_order', () => { fetchOrders(); });
      socket.on('order_updated', () => { fetchOrders(); });
      return () => {
        socket.off('new_order');
        socket.off('order_updated');
      };
    }
  }, [socket]);

  const fetchOrders = async () => {
    try {
      const response = await api.getOrderHistory();
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this order record? This cannot be undone.')) return;
    try {
      await api.deleteOrderHistory(id);
      toast.success('Order deleted');
      setOrders(orders.filter(o => o.id !== id));
    } catch {
      toast.error('Failed to delete order');
    }
  };

  const handleCreateBill = (order) => {
    // Pass order data to billing page via navigation state
    navigate('/admin/billing', {
      state: {
        prefill: {
          table_number: order.table_number,
          items: order.items.map(item => ({
            menu_item_id: item.menu_item_id,
            name: item.name,
            price: item.price.toString(),
            quantity: item.quantity,
          })),
          subtotal: order.subtotal,
        }
      }
    });
    toast.success(`Order from Table ${order.table_number} loaded into billing`);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-red-100 text-red-700 border-red-200',
      preparing: 'bg-orange-100 text-orange-700 border-orange-200',
      ready: 'bg-green-100 text-green-700 border-green-200',
      delivered: 'bg-gray-100 text-gray-600 border-gray-200',
      cancelled: 'bg-red-50 text-red-500 border-red-200'
    };
    return colors[status] || colors.pending;
  };

  const totalRevenue = orders
    .filter(o => o.status === 'delivered')
    .reduce((sum, order) => sum + (order.subtotal || 0), 0);

  if (loading) return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto animate-pulse">
        <div className="h-8 w-48 bg-gray-200 rounded-lg mb-2" />
        <div className="h-4 w-72 bg-gray-100 rounded mb-6" />
        {/* Stats skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
              <div className="h-8 w-20 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
        {/* Table skeleton */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="h-12 bg-gray-50 border-b border-gray-200" />
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex gap-6 px-6 py-4 border-b border-gray-100">
              <div className="h-5 w-12 bg-gray-200 rounded" />
              <div className="h-5 w-32 bg-gray-100 rounded" />
              <div className="h-5 w-20 bg-gray-100 rounded" />
              <div className="h-5 w-20 bg-gray-200 rounded-full" />
              <div className="h-5 w-24 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900">Order History</h2>
          <p className="text-gray-600 mt-1">View all completed orders. Click "Create Bill" to convert any order into a bill.</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg"><Hash className="w-5 h-5 text-blue-600" /></div>
              <span className="text-sm font-medium text-gray-600">Total Orders</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{orders.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg"><IndianRupee className="w-5 h-5 text-green-600" /></div>
              <span className="text-sm font-medium text-gray-600">Total Revenue</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">₹{totalRevenue.toFixed(2)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg"><Clock className="w-5 h-5 text-orange-600" /></div>
              <span className="text-sm font-medium text-gray-600">Cancelled Orders</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{orders.filter(o => o.status === 'cancelled').length}</p>
          </div>
        </div>

        {/* Orders Table */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No orders yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Table', 'Items', 'Subtotal', 'Status', 'Cancelled By', 'Date & Time', 'Actions'].map(h => (
                      <th key={h} className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {orders.map(order => (
                    <tr key={order.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <span className="text-lg font-bold text-gray-900">#{order.table_number}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="text-gray-900">{item.name} x{item.quantity}</div>
                          ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-lg font-semibold text-[#E65100]">
                          ₹{(order.subtotal || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${getStatusColor(order.status)}`}>
                          {order.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {order.cancelled_by ? (
                          <span className="text-sm text-red-500 font-medium capitalize">{order.cancelled_by}</span>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(order.created_at), 'MMM dd, yyyy')}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                          <Clock className="w-4 h-4" />
                          {format(new Date(order.created_at), 'HH:mm:ss')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {order.status === 'delivered' && (
                            <button
                              onClick={() => handleCreateBill(order)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E65100] hover:bg-[#D84800] text-white text-xs font-semibold rounded-lg transition"
                              title="Convert to bill"
                            >
                              <ReceiptText className="w-3.5 h-3.5" />
                              Create Bill
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="text-red-400 hover:text-red-600 transition"
                            title="Delete order"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistory;

